import React, { useEffect, useState } from 'react';
import { eligibilityRulesAPI, eligibilityRulesTicgnAPI } from '../lib/api';
import { Search, Info } from 'lucide-react';

const EligibilityRules = () => {
  const [rules, setRules] = useState([]);
  const [ticgnRulesMap, setTicgnRulesMap] = useState({});
  const [filteredRules, setFilteredRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEligibilite, setFilterEligibilite] = useState('');
  const [filterMotif, setFilterMotif] = useState('');

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async (searchTerm = '') => {
    try {
      const [ticfeRes, ticgnRes] = await Promise.all([
        eligibilityRulesAPI.list(searchTerm),
        eligibilityRulesTicgnAPI.list(searchTerm)
      ]);
      setRules(ticfeRes.data || []);
      setFilteredRules(ticfeRes.data || []);
      const map = {};
      (ticgnRes.data || []).forEach(r => { map[r.naf_code_label] = r; });
      setTicgnRulesMap(map);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Appliquer filtres
  useEffect(() => {
    let filtered = [...rules];
    
    if (filterEligibilite) {
      filtered = filtered.filter(r => r.eligibility_type === filterEligibilite);
    }
    
    if (filterMotif) {
      filtered = filtered.filter(r => r.eligibility_reason && r.eligibility_reason.includes(filterMotif));
    }
    
    setFilteredRules(filtered);
  }, [filterEligibilite, filterMotif, rules]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRules(search);
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#397DFF]"></div></div>;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-semibold text-[#191D1E] tracking-tight">Référentiel Codes NAF</h1>
        <p className="text-[#687175] mt-2">Consultation des codes NAF/APE et criteres d'eligibilite TICFE et TICGN (lecture seule)</p>
      </div>

      <div className="mb-6 p-4 bg-[#E8F4FD] border border-[#397DFF] rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-[#397DFF] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-[#191D1E]">
          Cette page est en <strong>lecture seule</strong>. 
          Pour modifier les codes NAF, utilisez la page <strong>"Admin Codes NAF"</strong> (accès administrateur).
        </p>
      </div>

      {/* Recherche et Filtres */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-6 mb-6">
        <form onSubmit={handleSearch} className="mb-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#687175]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par code NAF ou libellé..."
                className="w-full pl-12 pr-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
              />
            </div>
            <button type="submit" className="px-6 py-3 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium rounded-lg">
              Rechercher
            </button>
          </div>
        </form>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-[#BFC6CC]">
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Filtrer par Éligibilité Potentielle</label>
            <select
              value={filterEligibilite}
              onChange={(e) => setFilterEligibilite(e.target.value)}
              className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] text-sm"
            >
              <option value="">Toutes les éligibilités</option>
              <option value="EXONERATION">Exonération totale</option>
              <option value="TARIF_REDUIT">Tarif réduit</option>
              <option value="NON_CONCERNE">Non concerné</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Filtrer par Motif</label>
            <select
              value={filterMotif}
              onChange={(e) => setFilterMotif(e.target.value)}
              className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] text-sm"
            >
              <option value="">Tous les motifs</option>
              <option value="électro-intensive">Activité électro-intensive</option>
              <option value="fuite de carbone">Fuite de carbone</option>
              <option value="Minéralogie">Minéralogie</option>
              <option value="Chimie">Chimie</option>
              <option value="électrolyse">Procédé électrolyse</option>
            </select>
          </div>
        </div>

        {(filterEligibilite || filterMotif) && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-[#687175]">Filtres actifs :</span>
            {filterEligibilite && (
              <button onClick={() => setFilterEligibilite('')} className="px-3 py-1 bg-[#397DFF]/10 text-[#397DFF] rounded-full text-xs hover:bg-[#397DFF]/20">
                {filterEligibilite} ×
              </button>
            )}
            {filterMotif && (
              <button onClick={() => setFilterMotif('')} className="px-3 py-1 bg-[#F9C80E]/10 text-[#F9C80E] rounded-full text-xs hover:bg-[#F9C80E]/20">
                {filterMotif} ×
              </button>
            )}
          </div>
        )}
      </div>

      {filteredRules.length === 0 ? (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-12 text-center">
          <p className="text-[#687175]">Aucun code NAF trouvé</p>
        </div>
      ) : (
        <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Code NAF / Libellé</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">TICFE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Motif TICFE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">TICGN</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Motif TICGN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#BFC6CC]">
              {filteredRules.map((rule) => {
                  const ticgnRule = ticgnRulesMap[rule.naf_code_label];
                  return (
                  <tr key={rule.id} className="hover:bg-[#F5F6F9] transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-mono text-[#191D1E] font-semibold">{rule.naf_code_label}</div>
                      <div className="text-sm text-[#687175] mt-1">{rule.libelle}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        rule.eligibility_type === 'EXONERATION' ? 'bg-[#10D183]/10 text-[#10D183] border-[#10D183]/20' :
                        rule.eligibility_type === 'TARIF_REDUIT' ? 'bg-[#397DFF]/10 text-[#397DFF] border-[#397DFF]/20' :
                        'bg-[#687175]/10 text-[#687175] border-[#687175]/20'
                      }`}>
                        {rule.eligibility_type === 'EXONERATION' ? 'Exonération' :
                         rule.eligibility_type === 'TARIF_REDUIT' ? 'Tarif réduit' :
                         'Non concerné'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#191D1E] text-sm">
                      {rule.eligibility_reason || '-'}
                    </td>
                    <td className="px-6 py-4">
                      {ticgnRule ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          ticgnRule.eligibility_type === 'EXONERATION' ? 'bg-[#10D183]/10 text-[#10D183] border-[#10D183]/20' :
                          ticgnRule.eligibility_type === 'TARIF_REDUIT' ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/20' :
                          'bg-[#687175]/10 text-[#687175] border-[#687175]/20'
                        }`}>
                          {ticgnRule.eligibility_type === 'EXONERATION' ? 'Exonération' :
                           ticgnRule.eligibility_type === 'TARIF_REDUIT' ? 'Tarif réduit' :
                           'Non concerné'}
                        </span>
                      ) : (
                        <span className="text-xs text-[#BFC6CC]">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#191D1E]">
                      {ticgnRule?.eligibility_reason || '-'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 bg-[#F5F6F9] border-t border-[#BFC6CC] text-sm text-[#687175]">
            {filteredRules.length} code(s) affichés {filteredRules.length !== rules.length && `sur ${rules.length} total`}
          </div>
        </div>
      )}
    </div>
  );
};

export default EligibilityRules;
